/**
 * RecordingsPage Component - Recordings management
 *
 * Features:
 * - List all recordings
 * - Download individual tracks
 * - Upload to cloud
 * - Delete recordings
 * - Search and filter
 * - Publish to hosting
 * - Open in PodOps Studio editor
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudioStore } from '../App';
import {
  EditorRecording,
  EditorTrack,
  listEditorRecordings,
  editorBridge
} from '../lib/editor-integration';
import { recordingsApi } from '../lib/api';

export function RecordingsPage() {
  const navigate = useNavigate();
  const { token, userId } = useStudioStore();

  const [recordings, setRecordings] = useState<EditorRecording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecording, setSelectedRecording] = useState<EditorRecording | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Fetch recordings on mount
  useEffect(() => {
    fetchRecordings();
  }, [token, userId]);

  const fetchRecordings = async () => {
    try {
      setLoading(true);
      const result = await listEditorRecordings({ hostId: userId || '' });
      setRecordings(result.recordings || []);
    } catch (err) {
      console.error('[RecordingsPage] Fetch failed:', err);
      setError('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  // Format duration
  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Delete recording
  const handleDelete = async (recordingId: string) => {
    try {
      await recordingsApi.delete(recordingId);
      setRecordings(prev => prev.filter(r => r.id !== recordingId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('[RecordingsPage] Delete failed:', err);
      setError('Failed to delete recording');
    }
  };

  // Download track
  const handleDownload = async (track: EditorTrack) => {
    if (track.url) {
      window.open(track.url, '_blank');
    }
  };

  // Publish to hosting
  const handlePublish = (recording: EditorRecording) => {
    navigate(`/publish-episode?recordingId=${recording.id}`);
  };

  // Open in editor
  const handleEdit = async (recording: EditorRecording) => {
    await editorBridge.openEditor(recording.id);
  };

  // Filter recordings
  const filteredRecordings = recordings.filter(r =>
    r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.metadata?.hostName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get status badge
  const getStatusBadge = (status: EditorRecording['status']) => {
    switch (status) {
      case 'ready':
        return <span className="status-badge ready">✅ Ready</span>;
      case 'processing':
        return <span className="status-badge processing">⏳ Processing</span>;
      case 'uploading':
        return <span className="status-badge uploading">☁️ Uploading</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  return (
    <div className="recordings-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>🎬 Recordings</h1>
          <p className="subtitle">{recordings.length} recording{recordings.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="header-right">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search recordings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button className="new-session-btn" onClick={() => navigate('/')}>
            ➕ New Session
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="recordings-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading recordings...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <span>⚠️</span>
            <p>{error}</p>
            <button onClick={fetchRecordings}>Retry</button>
          </div>
        ) : filteredRecordings.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🎙️</span>
            <h2>No recordings yet</h2>
            <p>Start a new recording session to see your recordings here</p>
            <button className="start-btn" onClick={() => navigate('/')}>
              🎬 Start Recording
            </button>
          </div>
        ) : (
          <div className="recordings-grid">
            {filteredRecordings.map(recording => (
              <div
                key={recording.id}
                className="recording-card"
                onClick={() => setSelectedRecording(recording)}
              >
                {/* Thumbnail */}
                <div className="recording-thumbnail">
                  {recording.thumbnail ? (
                    <img src={recording.thumbnail} alt={recording.title} />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <span>🎥</span>
                    </div>
                  )}
                  <div className="duration-badge">
                    {formatDuration(recording.duration)}
                  </div>
                </div>

                {/* Info */}
                <div className="recording-info">
                  <h3 className="recording-title">{recording.title}</h3>
                  <p className="recording-date">{formatDate(recording.createdAt)}</p>
                  <p className="recording-participants">
                    👥 {recording.metadata?.participantCount || 1} participant{(recording.metadata?.participantCount || 1) !== 1 ? 's' : ''}
                  </p>
                  <div className="recording-meta">
                    {getStatusBadge(recording.status)}
                    <span className="track-count">
                      🎵 {recording.tracks.length} track{recording.tracks.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="recording-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="action-btn edit"
                    onClick={() => handleEdit(recording)}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    className="action-btn publish"
                    onClick={() => handlePublish(recording)}
                    title="Publish"
                  >
                    📤
                  </button>
                  <button
                    className="action-btn delete"
                    onClick={() => setDeleteConfirm(recording.id)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recording Detail Modal */}
      {selectedRecording && (
        <div className="modal-overlay" onClick={() => setSelectedRecording(null)}>
          <div className="recording-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedRecording.title}</h2>
              <button className="close-btn" onClick={() => setSelectedRecording(null)}>✕</button>
            </div>

            <div className="modal-content">
              <div className="recording-details">
                <p><strong>Date:</strong> {formatDate(selectedRecording.createdAt)}</p>
                <p><strong>Duration:</strong> {formatDuration(selectedRecording.duration)}</p>
                <p><strong>Participants:</strong> {selectedRecording.metadata?.hostName || 'Host'}{selectedRecording.metadata?.participantCount > 1 ? ` + ${selectedRecording.metadata.participantCount - 1} guest${selectedRecording.metadata.participantCount > 2 ? 's' : ''}` : ''}</p>
                <p><strong>Status:</strong> {getStatusBadge(selectedRecording.status)}</p>
              </div>

              <h3>🎵 Tracks</h3>
              <div className="tracks-list">
                {selectedRecording.tracks.map(track => (
                  <div key={track.id} className="track-item">
                    <div className="track-info">
                      <span className="track-icon">
                        {track.type === 'video' ? '🎥' : track.type === 'audio' ? '🎤' : '🖥️'}
                      </span>
                      <div className="track-details">
                        <span className="track-name">{track.participantName}</span>
                        <span className="track-duration">{track.duration ? formatDuration(track.duration) : '—'}</span>
                      </div>
                    </div>
                    <button
                      className="download-btn"
                      onClick={() => handleDownload(track)}
                      disabled={!track.url}
                    >
                      ⬇️ Download
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setSelectedRecording(null)}>
                Close
              </button>
              <button className="btn-primary" onClick={() => handlePublish(selectedRecording)}>
                📤 Publish to Hosting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Delete Recording?</h3>
            <p>This action cannot be undone. All tracks will be permanently deleted.</p>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
